import React, { useState, useEffect } from 'react';
import { ArrowRight, Building2, CheckCircle2, Globe, MapPin, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { clients, collaborations, statistics } from '../data/mock';

// Testimonials data
const testimonials = [
  {
    id: 1,
    quote: "ElemecH Engineering has been instrumental in optimizing our CHP operations. Their systematic approach to maintenance has reduced our unplanned downtime by over 40%, resulting in significant cost savings and improved plant efficiency.",
    name: "Senior Operations Manager",
    designation: "NTPC Thermal Power Station",
    location: "Maharashtra"
  },
  {
    id: 2,
    quote: "Working with ElemecH for our Wagon Tippler maintenance has been a game-changer. Their 24x7 support and quick response to breakdowns have drastically reduced our railway demurrage costs. A truly reliable partner.",
    name: "Chief Engineer",
    designation: "NSPCL Power Plant",
    location: "Odisha"
  },
  {
    id: 3,
    quote: "Their expertise in Stacker Reclaimer operations is unmatched. The team's proactive maintenance approach and technical knowledge have helped us maintain consistent coal supply without interruptions.",
    name: "Plant Head - CHP Division",
    designation: "State Power Generation Company",
    location: "Chhattisgarh"
  },
  {
    id: 4,
    quote: "ElemecH brings professionalism and reliability to every project. Their structured approach to complete CHP maintenance has improved our operational metrics and given us peace of mind for critical equipment.",
    name: "Deputy General Manager",
    designation: "Industrial Power Corporation",
    location: "Madhya Pradesh"
  }
];

const Clients = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 6000);
    return () => clearInterval(interval);
  }, [currentTestimonial]);

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (index) => {
    if (isAnimating || index === currentTestimonial) return;
    setIsAnimating(true);
    setCurrentTestimonial(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  return (
    <div className="bg-[#0b0b0b]">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px),
                             repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px)`
          }} />
        </div>
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#b00000] via-red-600 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Clients & Collaborations</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Trusted by Industry Leaders
            </h1>
            <p className="text-lg text-[#c0c0c0] leading-relaxed">
              For over three decades, India's leading power generation companies have trusted 
              ElemecH Engineering for their critical Coal Handling Plant maintenance and operational needs.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-12 border-t border-white/10 bg-[#b00000]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: "SINCE 1988", label: "Serving Clients" },
              { value: "35+", label: "Years of Trust" },
              { value: "NTPC & NSPCL", label: "Enlisted" },
              { value: "PAN INDIA", label: "Service Coverage" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-[#b00000] mb-1">{stat.value}</div>
                <div className="text-[#c0c0c0] text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organizations We Serve */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Organizations We Serve</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Major Clients
            </h2>
            <p className="text-[#c0c0c0] max-w-2xl mx-auto">
              We are proudly enlisted with major National Power Corporation units across India.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {clients.map((client, index) => (
              <div 
                key={index}
                className="bg-[#0b0b0b] border border-white/10 p-6 hover:border-[#b00000]/50 hover: hover:shadow-red-600/10 transition-all duration-300 group h-52"
              >
                <div className="bg-white w-full h-full flex items-center justify-center p-2">
                  <img 
                    src={client.logo} 
                    alt={client.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Collaborations */}
      <section className="py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Our Collaborations</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Global Partners & Authorized Dealer
            </h2>
            <p className="text-[#c0c0c0] max-w-2xl mx-auto">
              Strategic partnerships with leading global brands for superior quality components and solutions.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {collaborations.map((partner, index) => (
              <div key={index} className="bg-[#0b0b0b] border border-white/10 p-8 hover:border-[#b00000]/50 hover: hover:shadow-red-600/10 transition-all group">
                <div className="bg-white p-4 mb-6 h-24 flex items-center justify-center">
                  <img 
                    src={partner.logo}
                    alt={partner.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <div className="mb-4">
                  <h3 className="text-white font-bold text-xl mb-2">{partner.name}</h3>
                  <div className="flex items-center gap-1 text-[#b00000] text-sm mb-3">
                    <MapPin className="w-3 h-3" />
                    {partner.country}
                  </div>
                </div>
                <p className="text-[#c0c0c0] text-sm mb-4">{partner.description}</p>
                <div className="inline-block bg-[#b00000]/20 text-[#b00000] text-xs px-3 py-1">
                  Authorized Dealer
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Feedback Section */}
      <section className="py-12 sm:py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Client Feedback</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              What Our Clients Say
            </h2>
            <p className="text-sm sm:text-base text-[#c0c0c0] max-w-2xl mx-auto">
              What our clients say about working with us.
            </p>
          </div>

          {/* Testimonial Slider */}
          <div className="relative max-w-4xl mx-auto">
            {/* Navigation Arrows - Desktop */}
            <button
              onClick={handlePrev}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 lg:-translate-x-16 w-10 h-10 lg:w-12 lg:h-12 bg-[#0b0b0b] border border-white/10 items-center justify-center text-white/60 hover:text-white hover:border-[#b00000]/50 transition-all z-10"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6" />
            </button>
            <button
              onClick={handleNext}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 lg:translate-x-16 w-10 h-10 lg:w-12 lg:h-12 bg-[#0b0b0b] border border-white/10 items-center justify-center text-white/60 hover:text-white hover:border-[#b00000]/50 transition-all z-10"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6" />
            </button>

            {/* Testimonial Card */}
            <div className="overflow-hidden">
              <div
                className={`bg-[#0b0b0b] border border-white/10 p-6 sm:p-8 lg:p-10 transition-all duration-500 ${
                  isAnimating ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'
                }`}
              >
                {/* Quote Icon */}
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#b00000]/10 flex items-center justify-center">
                    <Quote className="w-6 h-6 sm:w-7 sm:h-7 text-[#b00000]" />
                  </div>
                </div>

                {/* Quote Text */}
                <blockquote className="text-base sm:text-lg lg:text-xl text-white/80 leading-relaxed text-center mb-6 sm:mb-8 italic">
                  "{testimonials[currentTestimonial].quote}"
                </blockquote>

                {/* Author Info */}
                <div className="text-center">
                  <div className="w-12 h-0.5 bg-[#b00000] mx-auto mb-4 sm:mb-6"></div>
                  <div className="text-white font-semibold text-sm sm:text-base mb-1">
                    {testimonials[currentTestimonial].name}
                  </div>
                  <div className="text-[#b00000] text-xs sm:text-sm mb-1">
                    {testimonials[currentTestimonial].designation}
                  </div>
                  <div className="text-white/40 text-xs sm:text-sm flex items-center justify-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {testimonials[currentTestimonial].location}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows - Mobile */}
            <div className="flex md:hidden justify-center gap-4 mt-6">
              <button
                onClick={handlePrev}
                className="w-10 h-10 bg-[#0b0b0b] border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-[#b00000]/50 transition-all"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 bg-[#0b0b0b] border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-[#b00000]/50 transition-all"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Dots Navigation */}
            <div className="flex justify-center gap-2 sm:gap-3 mt-6 sm:mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 sm:w-2.5 sm:h-2.5 transition-all duration-300 ${
                    index === currentTestimonial
                      ? 'bg-[#b00000] w-6 sm:w-8'
                      : 'bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Why Partner With Us</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The ElemecH Advantage
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              'SINCE 1988 - Proven Track Record',
              '35+ Years Technical Expertise',
              'NTPC & NSPCL Enlisted',
              'Reduced Demurrage Costs',
              '24x7 Operation Support',
              'Minimized Breakdowns',
              'Swift Issue Resolution',
              'Nationwide Service Network'
            ].map((advantage, index) => (
              <div key={index} className="bg-[#0b0b0b] border border-white/10 p-4 flex items-center gap-3 hover:border-[#b00000]/50 transition-colors">
                <CheckCircle2 className="w-5 h-5 text-[#b00000] flex-shrink-0" />
                <span className="text-white/70 text-sm">{advantage}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/10 bg-gradient-to-r from-[#b00000]/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Become Our Partner
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-8">
            Join the growing list of industry leaders who trust ElemecH Engineering 
            for their critical Coal Handling Plant operations.
          </p>
          <Link to="/contact">
            <Button className="bg-[#FFD700] hover:bg-[#FFC000] text-black font-semibold rounded-none none px-8 h-14 text-base transition-all duration-300 group">
              Partner With Us
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Clients;
